/* eslint-disable react-hooks/exhaustive-deps */
import React, {useState, useEffect, useRef} from "react"
import {keyBy, get, head, sortBy, isEmpty} from "lodash"
import Draggable from "react-draggable"
import {ResizableBox} from "react-resizable"
import cx from "classnames"
import randomColor from "randomcolor"
import {Icon} from "components/Icon"
import {DigitalDocumentFieldSetUp} from "types/tasks"
import {CheckboxForm, InitialForm, SignatureForm, TextForm, SelectForm, GradeForm, FileForm} from "../FieldBodyForm"
import {getDragBounds} from "../utils/fieldLayout"
import styles from "./FieldAction.module.css"

export function FieldAction({
    field,
    fields,
    fieldBodyContent,
    activeField,
    boundaryWidth,
    boundaryHeight,
    definedUsers,
    participants,
    combinedColor,
    scaleInfo,
    totalPage,
    previousFieldInfo,
    errorFieldIds,
    isEditable,
    onDragStart,
    onDrag,
    onDragStop,
    onResize,
    getIconByType,
    onClickDelete,
    setActiveField,
    onChangeField,
    onAddNewFieldValue,
    onRemoveFieldValue,
    onClickField,
    onChangeFieldValue,
    onChangeCombinedColor,
    onDuplicateField,
    selectedFieldIds,
    groupDrag,
    onToggleFieldSelection,
    onSelectOnlyField
}) {
    const [isShowContent, setIsShowContent] = useState(false)
    const [color, setColor] = useState("")
    const contentRef = useRef()
    const isDraggingRef = useRef(false)
    const icon = getIconByType(field.type)
    const isActive = activeField && field.id === activeField.id
    const isMultiSelected = selectedFieldIds.includes(field.id) && selectedFieldIds.length > 1
    const draggedPosition = groupDrag?.starts?.[field.id]
    const positionX = draggedPosition ? draggedPosition.left + groupDrag.dx : field.placeInformation.left || 0
    const positionY = draggedPosition ? draggedPosition.top + groupDrag.dy : field.placeInformation.top || 0
    const dragBounds = getDragBounds(field, fields, selectedFieldIds, boundaryWidth, boundaryHeight)
    const parentField = fields.find((item) => item.id === field.parentId)
    const dependencyFieldUuids = (activeField?.dependencies || []).map((item) => item?.uuid?.uuid).filter(Boolean)

    useEffect(() => {
        getFieldActionColor()
    }, [field.signatureTypes, field.parentSignatureTypeId, combinedColor])

    useEffect(() => {
        if (activeField && field.id === activeField.id) {
            setIsShowContent(true)
        }
    }, [])

    useEffect(() => {
        const handleClickOutside = (e) => {
            const currentRefEl = contentRef.current || null
            if (isShowContent && contentRef.current && !currentRefEl.contains(e.target)) {
                setIsShowContent(false)
                setActiveField(null)
            }
        }
        document.addEventListener("mousedown", handleClickOutside)
        return () => {
            document.removeEventListener("mousedown", handleClickOutside)
        }
    }, [isShowContent])

    const getFieldActionColor = () => {
        const signatureTypeIds = (field.signatureTypes || []).map((item) => item.id)
        let color = ""
        if (signatureTypeIds.length > 1) {
            const sortedSignerIds = sortBy(signatureTypeIds).join("")
            if (!combinedColor[sortedSignerIds]) {
                color = randomColor({luminosity: "bright", format: "rgba", alpha: 0.5})
                onChangeCombinedColor(sortedSignerIds, color)
            } else {
                color = combinedColor[sortedSignerIds]
            }
        } else {
            const firstSignatureTypeId = head(signatureTypeIds)
            const signatureTypeId = firstSignatureTypeId || field.parentSignatureTypeId
            const definedUsersKeyById = keyBy(definedUsers, "id")
            color = get(definedUsersKeyById[signatureTypeId], "color", "#1e90ff")
        }
        setColor(color)
    }

    const onClick = (event) => {
        const isMultiSelect = Boolean(event?.shiftKey || event?.metaKey || event?.ctrlKey)
        if (isEditable && isMultiSelect) {
            onToggleFieldSelection(field)
            setIsShowContent(false)
            return
        }
        onClickField(field)
        if (isEditable) {
            const nextField = isShowContent ? null : field
            setIsShowContent(!isShowContent)
            onSelectOnlyField(nextField)
            return
        }
        setActiveField(field)
        setIsShowContent(true)
    }

    const dragStart = (event, data) => {
        onDragStart(event, data, field)
    }

    const dragStop = (event, data) => {
        const wasDragging = isDraggingRef.current
        isDraggingRef.current = false
        onDragStop(event, data, field, wasDragging)
        if (!wasDragging) {
            onClick(event)
        }
    }

    const handleDrag = (event, data) => {
        if (data.deltaX !== 0 || data.deltaY !== 0) {
            isDraggingRef.current = true
        }
        onDrag(event, data, field)
    }

    const handleDuplicateField = (field) => {
        onDuplicateField(field)
        setIsShowContent(false)
        setActiveField(null)
    }

    const renderBodyContent = () => {
        switch (field.type) {
            case DigitalDocumentFieldSetUp.SIGNATURE:
                return (
                    <SignatureForm
                        field={fieldBodyContent}
                        fields={fields}
                        definedUsers={definedUsers}
                        scaleInfo={scaleInfo}
                        totalPage={totalPage}
                        previousFieldInfo={previousFieldInfo}
                        isEditable={isEditable}
                        onClickDelete={onClickDelete}
                        onChangeField={onChangeField}
                        onAddNewFieldValue={onAddNewFieldValue}
                        onRemoveFieldValue={onRemoveFieldValue}
                        onDuplicateField={handleDuplicateField}
                    />
                )
            case DigitalDocumentFieldSetUp.INITIAL:
                return (
                    <InitialForm
                        field={fieldBodyContent}
                        fields={fields}
                        definedUsers={definedUsers}
                        scaleInfo={scaleInfo}
                        totalPage={totalPage}
                        previousFieldInfo={previousFieldInfo}
                        isEditable={isEditable}
                        activeField={activeField}
                        onClickDelete={onClickDelete}
                        onChangeField={onChangeField}
                        onAddNewFieldValue={onAddNewFieldValue}
                        onRemoveFieldValue={onRemoveFieldValue}
                        onChangeFieldValue={onChangeFieldValue}
                        onDuplicateField={handleDuplicateField}
                    />
                )
            case DigitalDocumentFieldSetUp.TEXT:
                return (
                    <TextForm
                        field={fieldBodyContent}
                        fields={fields}
                        definedUsers={definedUsers}
                        participants={participants}
                        scaleInfo={scaleInfo}
                        totalPage={totalPage}
                        isEditable={isEditable}
                        onClickDelete={onClickDelete}
                        onChangeField={onChangeField}
                        onDuplicateField={handleDuplicateField}
                    />
                )
            case DigitalDocumentFieldSetUp.CHECKBOX:
                return (
                    <CheckboxForm
                        field={fieldBodyContent}
                        fields={fields}
                        definedUsers={definedUsers}
                        scaleInfo={scaleInfo}
                        totalPage={totalPage}
                        previousFieldInfo={previousFieldInfo}
                        isEditable={isEditable}
                        activeField={activeField}
                        onClickDelete={onClickDelete}
                        onChangeField={onChangeField}
                        onAddNewFieldValue={onAddNewFieldValue}
                        onRemoveFieldValue={onRemoveFieldValue}
                        onChangeFieldValue={onChangeFieldValue}
                        onDuplicateField={handleDuplicateField}
                    />
                )
            case DigitalDocumentFieldSetUp.SELECT:
                return (
                    <SelectForm
                        field={fieldBodyContent}
                        fields={fields}
                        definedUsers={definedUsers}
                        scaleInfo={scaleInfo}
                        totalPage={totalPage}
                        isEditable={isEditable}
                        activeField={activeField}
                        onClickDelete={onClickDelete}
                        onChangeField={onChangeField}
                        onAddNewFieldValue={onAddNewFieldValue}
                        onRemoveFieldValue={onRemoveFieldValue}
                        onChangeFieldValue={onChangeFieldValue}
                        onDuplicateField={handleDuplicateField}
                    />
                )
            case DigitalDocumentFieldSetUp.GRADE:
                return (
                    <GradeForm
                        field={fieldBodyContent}
                        fields={fields}
                        definedUsers={definedUsers}
                        scaleInfo={scaleInfo}
                        totalPage={totalPage}
                        isEditable={isEditable}
                        onClickDelete={onClickDelete}
                        onChangeField={onChangeField}
                        onDuplicateField={handleDuplicateField}
                    />
                )
            case DigitalDocumentFieldSetUp.FILE:
                return (
                    <FileForm
                        field={fieldBodyContent}
                        fields={fields}
                        definedUsers={definedUsers}
                        scaleInfo={scaleInfo}
                        totalPage={totalPage}
                        isEditable={isEditable}
                        onClickDelete={onClickDelete}
                        onChangeField={onChangeField}
                        onDuplicateField={handleDuplicateField}
                    />
                )
            default:
                return null
        }
    }

    const renderContent = () => {
        return (
            <div
                data-field-action
                className={cx(styles.wrap, {
                    [styles.fillOut]: isShowContent,
                    [styles.multiSelectedWrap]: isMultiSelected
                })}>
                <ResizableBox
                    width={field.placeInformation.width || 32}
                    height={field.placeInformation.height || 32}
                    className={styles.resizableBox}
                    minConstraints={[12, 12]}
                    maxConstraints={[1400, 400]}
                    handle={isEditable ? <span className={styles.customHandleResizable} /> : null}
                    onResize={(event, data) => onResize(event, data, field)}>
                    <div
                        className={cx(styles.fieldAction, styles.resizableItem, "handle", {
                            [styles.fieldActionSet]: !isActive && !isMultiSelected,
                            [styles.selectedField]: isMultiSelected,
                            [styles.hasConditionalRules]:
                                !isEmpty(field.dependencies) || !isEmpty(parentField?.dependencies),
                            [styles.dependencyField]:
                                !isEmpty(dependencyFieldUuids) &&
                                (dependencyFieldUuids.includes(field.uuid) ||
                                    dependencyFieldUuids.includes(parentField?.uuid)),
                            [styles.errorField]: (errorFieldIds || []).includes(field.id)
                        })}
                        style={{background: color}}>
                        <div className={styles.indicatorWrap}>
                            {(field.signatureTypes || []).map((_, index) => (
                                <div className={styles.indicator} key={index}></div>
                            ))}
                        </div>
                        <Icon icon={icon} color="#000" className={styles.icon} />
                    </div>
                </ResizableBox>
                {isShowContent && <div ref={contentRef}>{renderBodyContent()}</div>}
            </div>
        )
    }

    if (!isEditable) {
        return (
            <div
                className={cx({[styles.fillOut]: isShowContent})}
                style={{
                    transform: `translate(${field.placeInformation.left}px, ${field.placeInformation.top}px)`,
                    position: "relative"
                }}
                onClick={onClick}>
                {renderContent()}
            </div>
        )
    }

    return (
        <Draggable
            bounds={dragBounds}
            handle=".handle"
            position={{x: positionX, y: positionY}}
            disabled={!isEditable}
            onStart={dragStart}
            onStop={dragStop}
            onDrag={handleDrag}>
            {renderContent()}
        </Draggable>
    )
}
